import React, { useState, useEffect, useMemo, useRef } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import hamIcon from '../../assets/ham-icon.svg';
import { PopoverMenuComponent } from '../ui/PopoverMenuComponent';
import { Course } from '../../types/course';
import {
  UserCourse,
  LessonType,
  LessonResponse,
  QuizProgress,
  getQuizProgress,
  updateQuizLesson,
  createNewQuizLesson,
  calculateCourseProgress,
} from '../../types/userCourse';
import {
  Quiz,
  QuizQuestion as APIQuizQuestion,
  NormalizedQuizQuestion,
  NormalizedQuizOption
} from '../../types/quiz';
import { courseService } from '../../services/courseService';
import { userCourseService } from '../../services/userCourseService';
import { showError } from '../../services/toastService';
import { useProfileStore } from '../../store/useProfileStore';
import { progressService } from '../../services/progressService';
import { badgeModalService } from '../../services/badgeModalService';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';

// ============================================================================
// TYPES & MODELS
// ============================================================================

interface LessonQuizProps {
  course: Course;
  userCourse: UserCourse | null;
  onProgressUpdate?: (userCourse: UserCourse) => void;
  progressToNext?: () => void;
  onToggleSidebar?: () => void;
  hasNextLesson: boolean;
  hasPreviousLesson: boolean;
  onNavigateToNext?: () => void;
  onNavigateToPrevious?: () => void;
  stepNumber: number;
  totalSteps: number;
  nextStepLabel?: string;
}

export const LessonQuiz: React.FC<LessonQuizProps> = ({
  course,
  userCourse,
  onProgressUpdate,
  progressToNext,
  onToggleSidebar,
  hasNextLesson,
  hasPreviousLesson,
  onNavigateToNext,
  onNavigateToPrevious,
  stepNumber,
  totalSteps,
  nextStepLabel
}) => {
  const sessionStartRef = useRef<Date>(new Date());

  // Determine lesson status based on userCourse data
  const lessonStatus = React.useMemo(() => {
    if (!userCourse) return 'Not Started';

    const lesson = userCourse.userLessons.find(
      l => l.lessonId === course.quizId && l.lessonType === LessonType.QUIZ
    );

    if (!lesson || !lesson.startedAt) return 'Not Started';
    if (lesson.completedAt) return 'Completed';
    return 'In Progress';
  }, [userCourse, course.quizId]);

  // Get status styling
  const getStatusStyle = () => {
    switch (lessonStatus) {
      case 'Completed':
        return 'bg-green-500 text-white';
      case 'In Progress':
        return 'bg-red-500 text-white';
      case 'Not Started':
        return 'bg-medium';
      default:
        return 'bg-medium';
    }
  };

  // Handle navigation with confirmation if lesson is in progress
  const handleNavigationClick = (direction: 'next' | 'previous', event: React.MouseEvent) => {
    if (lessonStatus === 'In Progress') {
      setNavigationDirection(direction);
      setNavigationPopoverEvent(event.nativeEvent);
      setShowNavigationPopover(true);
    } else {
      // If not in progress, navigate directly
      if (direction === 'next') {
        onNavigateToNext?.();
      } else {
        onNavigateToPrevious?.();
      }
    }
  };

  const handlePopoverDismiss = (selectedKey?: string) => {
    setShowNavigationPopover(false);
    setNavigationPopoverEvent(undefined);

    if (selectedKey === 'confirm') {
      // User confirmed navigation
      if (navigationDirection === 'next') {
        onNavigateToNext?.();
      } else if (navigationDirection === 'previous') {
        onNavigateToPrevious?.();
      }
    }
    setNavigationDirection(null);
  };

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({}); // valid option ID (A, B, C...)
  const [isRetryMode, setIsRetryMode] = useState(false);
  const [incorrectQuestions, setIncorrectQuestions] = useState<number[]>([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showResultScreen, setShowResultScreen] = useState(false);
  const [lastSavedAnswers, setLastSavedAnswers] = useState<Record<number, string>>({}); // Track which answers were already saved
  const [showNavigationPopover, setShowNavigationPopover] = useState(false);
  const [navigationPopoverEvent, setNavigationPopoverEvent] = useState<Event | undefined>(undefined);
  const [navigationDirection, setNavigationDirection] = useState<'next' | 'previous' | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const saveInProgressRef = useRef(false);
  const pendingSaveRef = useRef<{ answers: Record<number, string>; questionIndex?: number; attemptOverride?: number; isComplete?: boolean } | null>(null);
  const hasCheckedBadgesRef = useRef<boolean>(false);
  const skipResumeOnNextUserCourseRef = useRef(false);
  
  const profile = useProfileStore((state) => state.activeProfile);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        const quizData = await courseService.getQuiz(course.id);
        setQuiz(quizData);
      } catch (err) {
        // Error modal presented by API
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [course.id]);

  // ============================================================================
  // RESUME PROGRESS ON LOAD
  // ============================================================================

  // Track if we've already initialized to prevent re-initialization on userCourse updates
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!userCourse || !course.quizId || !quiz) return;
    if (hasInitialized.current) return;

    if (skipResumeOnNextUserCourseRef.current) {
      skipResumeOnNextUserCourseRef.current = false;
      return;
    }

    const existingLesson = userCourse.userLessons.find(
      lesson => lesson.lessonId === course.quizId && lesson.lessonType === LessonType.QUIZ
    );

    if (!existingLesson) {
      // New quiz - start at attempt 1
      setAttemptNumber(1);
      hasInitialized.current = true;
      return;
    }

    const savedProgress = getQuizProgress(existingLesson);
    if (!savedProgress) return;

    // Initialize attempt number from saved progress
    setAttemptNumber(savedProgress.attempt);

    if (savedProgress.isComplete) {
      setShowResultScreen(true);
      setIsCompleted(true);
      hasInitialized.current = true;
      return;
    }

    if (savedProgress.currentQuestionIndex > 0) {
      setCurrentQuestionIndex(savedProgress.currentQuestionIndex);
    }

    // Restore saved answers from lessonResponses for current attempt
    if (userCourse.lessonResponses && userCourse.lessonResponses.length > 0) {
      const currentAttempt = savedProgress.attempt;
      const quizResponses = userCourse.lessonResponses.filter(
        r => r.lessonId === course.quizId &&
             r.lessonType === LessonType.QUIZ &&
             r.attempt === currentAttempt
      );

      if (quizResponses.length > 0) {
        const restoredAnswers: Record<number, string> = {};
        quizResponses.forEach(response => {
          restoredAnswers[response.itemNumber] = response.response;
        });
        setUserAnswers(restoredAnswers);
        setLastSavedAnswers(restoredAnswers);
      }
    }

    hasInitialized.current = true;
  }, [quiz, userCourse, course.quizId]);

  // ============================================================================
  // DATA PARSING
  // ============================================================================

  // Transform API Quiz to Internal Format
  const questions: NormalizedQuizQuestion[] = useMemo(() => {
    if (!quiz) return [];

    return quiz.quizQuestions.map((q: APIQuizQuestion) => {
      // Convert options array to normalized format
      const optionsArray: NormalizedQuizOption[] = q.options.map(opt => ({
        id: opt.option,
        text: opt.answer,
        isCorrect: opt.option === q.correctOption,
        explanation: opt.explanation
      }));

      // Find the correct option's explanation for correctExplanation
      const correctOption = q.options.find(opt => opt.option === q.correctOption);

      return {
        id: q.number,
        text: q.text,
        options: optionsArray,
        correctExplanation: correctOption?.explanation || "No explanation provided."
      };
    });
  }, [quiz]);

  // Filter questions provided based on retry mode
  const activeQuestions = useMemo(() => {
    if (isRetryMode) {
      // Only show questions that were answered incorrectly
      return questions.filter(q => incorrectQuestions.includes(q.id));
    }
    return questions;
  }, [questions, isRetryMode, incorrectQuestions]);

  const currentQuestion = activeQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === activeQuestions.length - 1;

  // Answer status for current question
  const currentAnswerId = currentQuestion ? userAnswers[currentQuestion.id] : undefined;
  const isCurrentAnswered = currentAnswerId !== undefined;

  // Determine if specific answer is correct
  const isAnswerCorrect = (questionId: number, answerId: string) => {
    const question = questions.find((q: NormalizedQuizQuestion) => q.id === questionId);
    return question?.options.find((o: NormalizedQuizOption) => o.id === answerId)?.isCorrect ?? false;
  };

  const isCurrentCorrect = isCurrentAnswered && isAnswerCorrect(currentQuestion.id, currentAnswerId!);

  // Calculate progress based on answered questions
  const currentProgress = useMemo(() => {
    // Check if lesson is completed from saved data
    const lesson = userCourse?.userLessons.find(
      l => l.lessonId === course.quizId && l.lessonType === LessonType.QUIZ
    );

    // If completed, always show 100%
    if (lesson?.completedAt) {
      return { percentComplete: 100, isComplete: true };
    }

    const answeredCount = Object.keys(userAnswers).length;
    const percentComplete = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;
    return {
      percentComplete,
      isComplete: isCompleted
    };
  }, [userAnswers, questions.length, isCompleted, userCourse, course.quizId]);

  // ============================================================================
  // PROGRESS TRACKING
  // ============================================================================

  // Save progress to UserCourse
  const saveProgressWithAnswers = async (answersToSave: Record<number, string>, questionIndex?: number, attemptOverride?: number, isCompleteOverride?: boolean) => {
    if (!course.quizId) return;

    // If a save is in progress, queue this save for later
    if (saveInProgressRef.current) {
      pendingSaveRef.current = { answers: answersToSave, questionIndex, attemptOverride, isComplete: isCompleteOverride };
      return;
    }

    saveInProgressRef.current = true;

    try {
      // Calculate correct responses count using provided answers
      let correctCount = 0;
      questions.forEach((q: NormalizedQuizQuestion) => {
        const answerId = answersToSave[q.id];
        if (answerId && isAnswerCorrect(q.id, answerId)) {
          correctCount++;
        }
      });

      // Calculate progress percentage
      const totalAnswered = Object.keys(answersToSave).length;
      const percentCorrect = questions.length > 0
        ? Math.round((correctCount / questions.length) * 100)
        : 0;

      // Use attemptOverride if provided (for new attempts), otherwise use state
      const currentAttempt = attemptOverride !== undefined ? attemptOverride : attemptNumber;

      // Create QuizProgress object
      const quizProgress: QuizProgress = {
        quizId: course.quizId,
        currentQuestionIndex: questionIndex !== undefined ? questionIndex : currentQuestionIndex,
        totalResponses: totalAnswered,
        correctResponses: correctCount,
        percentCorrect,
        isComplete: isCompleteOverride !== undefined ? isCompleteOverride : isCompleted,
        attempt: currentAttempt,
        lastAttempted: new Date().toISOString()
      };

      if (!userCourse) {
        showError('Cannot save video progress - user course data not loaded');
        return;
      }

      // Find existing quiz lesson
      const existingLesson = userCourse.userLessons.find(
        lesson => lesson.lessonId === course.quizId && lesson.lessonType === LessonType.QUIZ
      );

      // Update lesson with new progress
      const updatedLesson = updateQuizLesson(
        existingLesson,
        quizProgress,
        course.quizId,
        questions.length,
        sessionStartRef.current
      );

      // Update UserCourse with modified lesson
      const updatedLessons = existingLesson
        ? userCourse.userLessons.map(l =>
            l.lessonId === course.quizId && l.lessonType === LessonType.QUIZ
              ? updatedLesson
              : l
          )
        : [...userCourse.userLessons, updatedLesson];

      // Create LessonResponse objects for newly answered questions (not yet saved)
      const newResponses: LessonResponse[] = [];

      Object.entries(answersToSave).forEach(([questionIdStr, answerId]) => {
        const questionId = parseInt(questionIdStr);

        // Only create response if this answer wasn't saved before
        if (lastSavedAnswers[questionId] !== answerId) {
          const isCorrect = isAnswerCorrect(questionId, answerId);

          const response: LessonResponse = {
            lessonId: course.quizId,
            lessonType: LessonType.QUIZ,
            itemNumber: questionId,
            correctResponse: isCorrect,
            responseAt: new Date().toISOString(),
            response: answerId,
            attempt: currentAttempt
          };

          newResponses.push(response);
        }
      });

      // Update lastSavedAnswers to current state
      setLastSavedAnswers({ ...answersToSave });

      // Calculate overall course progress and lessons completed
      const { progress, lessonsCompleted } = calculateCourseProgress(
        updatedLessons,
        course.steps
      );

      const updatedUserCourse: UserCourse = {
        ...userCourse,
        userLessons: updatedLessons,
        progress,
        lessonsCompleted,
        lastAccessedAt: new Date().toISOString(),
        lastAccessedLesson: LessonType.QUIZ,
        lastAccessedLessonId: course.quizId,
        lessonResponses: [...(userCourse.lessonResponses || []), ...newResponses]
      };

      // Call service stub (will eventually persist to backend)
      await userCourseService.updateUserCourse(updatedUserCourse);

      skipResumeOnNextUserCourseRef.current = true;
      onProgressUpdate?.(updatedUserCourse);

      // Check for newly earned badges if course is completed (100% progress)
      // Only check once per completion to avoid duplicate checks
      if (progress >= 100 && !hasCheckedBadgesRef.current && profile?.id) {
        hasCheckedBadgesRef.current = true;
        // Fire and forget - fetch summary and check for new badges
        progressService.getTeamUserSummary(profile.id)
          .then(summary => {
            if (summary.badges) {
              badgeModalService.checkAndShowNewBadges(summary.badges, profile.id);
            }
          })
          .catch(err => {
            // Silent failure - don't interrupt user experience
            console.error('Failed to check for badges after quiz completion:', err);
          });
      }
    } finally {
      saveInProgressRef.current = false;

      // If there's a pending save, process it now
      if (pendingSaveRef.current) {
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        // Schedule on next tick to avoid recursion depth issues
        setTimeout(() => {
          saveProgressWithAnswers(pending.answers, pending.questionIndex, pending.attemptOverride, pending.isComplete);
        }, 0);
      }
    }
  };

  const saveProgress = async () => {
    await saveProgressWithAnswers(userAnswers);
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSelectAnswer = async (optionId: string) => {
    if (isCurrentAnswered) return; // Prevent changing answer after selection

    // Calculate new userAnswers immediately
    const newUserAnswers = {
      ...userAnswers,
      [currentQuestion.id]: optionId
    };

    setUserAnswers(newUserAnswers);

    // Save progress with the updated answers
    // Pass newUserAnswers to avoid stale closure
    setTimeout(() => saveProgressWithAnswers(newUserAnswers), 0);
  };

  const handleNext = () => {
    if (currentQuestionIndex < activeQuestions.length - 1) {
      // Increment position and update UI immediately
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);

      // Save progress in background (non-blocking)
      saveProgressWithAnswers(userAnswers, nextIndex);
    } else {
      onFinishRound();
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const onFinishRound = async () => {
    const newIncorrectIds: number[] = [];
    questions.forEach((q: NormalizedQuizQuestion) => {
      const answerId = userAnswers[q.id];
      const isCorrect = answerId && questions.find((qu: NormalizedQuizQuestion) => qu.id === q.id)?.options.find((o: NormalizedQuizOption) => o.id === answerId)?.isCorrect;
      if (!isCorrect) newIncorrectIds.push(q.id);
    });

    setIncorrectQuestions(newIncorrectIds);
    setShowResultScreen(true);

    if (newIncorrectIds.length === 0) {
      setIsCompleted(true);
      // Reset badge check flag when quiz completes
      hasCheckedBadgesRef.current = false;
      // Save final progress with completion - pass true explicitly to avoid async state issue
      await saveProgressWithAnswers(userAnswers, currentQuestionIndex, undefined, true);
      // Note: progressToNext() is called when user clicks "Continue to Next Lesson" button
    } else {
      // Save progress even if not completed
      await saveProgress();
    }
  };

  const startRetry = async () => {
    setIsRetryMode(true);
    setCurrentQuestionIndex(0);

    // Increment attempt number immediately
    const newAttempt = attemptNumber + 1;
    setAttemptNumber(newAttempt);

    // Reset session start time for new attempt
    sessionStartRef.current = new Date();

    // Keep correct answers, clear incorrect ones
    const answersToKeep: Record<number, string> = {};
    Object.entries(userAnswers).forEach(([questionIdStr, answerId]) => {
      const questionId = parseInt(questionIdStr);
      if (!incorrectQuestions.includes(questionId)) {
        answersToKeep[questionId] = answerId;
      }
    });
    setUserAnswers(answersToKeep);
    // Clear lastSavedAnswers so the carried-over answers get saved as new lessonResponses for the new attempt
    setLastSavedAnswers({});

    // Persist the new attempt immediately with current progress
    // Pass newAttempt to ensure it's saved with the incremented value
    await saveProgressWithAnswers(answersToKeep, 0, newAttempt);
    // Note: lastSavedAnswers is set inside saveProgressWithAnswers (line 358)
  };

  const startOver = async () => {
    setIsRetryMode(false);
    setIncorrectQuestions([]);
    setCurrentQuestionIndex(0);
    setIsCompleted(false);

    // Increment attempt number immediately
    const newAttempt = attemptNumber + 1;
    setAttemptNumber(newAttempt);

    // Reset session start time for fresh start
    sessionStartRef.current = new Date();

    // Clear all answers
    setUserAnswers({});
    setLastSavedAnswers({});

    // Persist the new attempt immediately with empty answers
    // Pass newAttempt to ensure it's saved with the incremented value
    await saveProgressWithAnswers({}, 0, newAttempt);
  };

  // ============================================================================
  // LOADING & ERROR STATES
  // ============================================================================

  if (loading) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-shrink-0 pt-4 px-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
              aria-label="Toggle menu"
            >
              <IonIcon icon={hamIcon} size="small" color="medium" />
            </button>
            <h1 className="text-2xl font-bold">{course.quizTitle}</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <IonSpinner name="crescent" />
        </div>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-shrink-0 pt-4 px-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
              aria-label="Toggle menu"
            >
              <IonIcon icon={hamIcon} size="small" color="medium" />
            </button>
            <h1 className="text-2xl font-bold">{course.quizTitle}</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-500">{error || 'Failed to load quiz'}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RESULT SCREENS
  // ============================================================================

  if (showResultScreen) {
    if (isCompleted) {
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="flex-shrink-0 pt-4 px-8">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={onToggleSidebar}
                className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
                aria-label="Toggle menu"
              >
                <IonIcon icon={hamIcon} size="small" color="medium" />
              </button>
              <h1 className="text-2xl font-bold">{course.quizTitle}</h1>
            </div>


            <div id="lesson-progress">
              {/* Lesson Progress Indicator */}
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-normal text-gray-600">STEP {stepNumber} OF {totalSteps}</span>
                </div>
                <div className="flex items-center">
                  <div className={`mx-2 px-2 py-1.5 mt-1 text-xs font-medium rounded ${getStatusStyle()}`}>
                    {lessonStatus}
                  </div>
                  <div className="flex gap-0.5">
                    <IonButton
                      className="toolbar-button-icon-small"
                      fill="solid"
                      size="small"
                      color="medium"
                      onClick={(e) => handleNavigationClick('previous', e)}
                    >
                      <IonIcon icon={chevronBackOutline} className="fs-16" color="secondary" />
                    </IonButton>
                    <IonButton
                      className="toolbar-button-icon-small"
                      fill="solid"
                      size="small"
                      color="medium"
                      disabled={!hasNextLesson}
                      onClick={(e) => handleNavigationClick('next', e)}
                    >
                      <IonIcon icon={chevronForwardOutline} className="fs-16" color="secondary" />
                    </IonButton>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="flex-1 overflow-y-auto px-8 pb-8">
            <div className="p-8 max-w-3xl mx-auto bg-white rounded-xl shadow-lg border-2 border-green-500 text-center animate-in fade-in duration-500">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold text-green-800 mb-2">Congratulations!</h2>
              <p className="text-green-700 text-lg mb-6">You have successfully completed this activity with 100% accuracy!</p>
              <button
                onClick={progressToNext}
                className="px-8 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg shadow-green-200"
              >
                {nextStepLabel || 'Continue to Next Step'}
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="flex-shrink-0 pt-4 px-8">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={onToggleSidebar}
                className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
                aria-label="Toggle menu"
              >
                <IonIcon icon={hamIcon} size="small" color="medium" />
              </button>
              <h1 className="text-2xl font-bold">{course.quizTitle}</h1>
            </div>

            <div id="lesson-progress">
              {/* Lesson Progress Indicator */}
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-normal text-gray-600">STEP {stepNumber} OF {totalSteps}</span>
                </div>
                <div className="flex items-center">
                  <div className={`mx-2 px-2 py-1.5 mt-1 text-xs font-medium rounded ${getStatusStyle()}`}>
                    {lessonStatus}
                  </div>
                  <div className="flex gap-0.5">
                    <IonButton
                      className="toolbar-button-icon-small"
                      fill="solid"
                      size="small"
                      color="medium"
                      onClick={(e) => handleNavigationClick('previous', e)}
                    >
                      <IonIcon icon={chevronBackOutline} className="fs-16" color="secondary" />
                    </IonButton>
                    <IonButton
                      className="toolbar-button-icon-small"
                      fill="solid"
                      size="small"
                      color="medium"
                      disabled={!hasNextLesson}
                      onClick={(e) => handleNavigationClick('next', e)}
                    >
                      <IonIcon icon={chevronForwardOutline} className="fs-16" color="secondary" />
                    </IonButton>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="flex-1 overflow-y-auto px-8 pb-8">
            <div className="p-8 max-w-3xl mx-auto bg-white rounded-xl shadow-lg border-2 border-[#ED1C24] text-center animate-in fade-in duration-500">
              <div className="text-6xl mb-4">📚</div>
              <h2 className="text-3xl font-bold mb-2" style={{ color: '#AD1419' }}>Let's Review</h2>
              <p className="text-lg mb-6" style={{ color: '#C41820' }}>You have {incorrectQuestions.length} incorrect answers. Let's review those questions again. Keep trying until you get them all correct.</p>
              <button
                onClick={() => {
                  setShowResultScreen(false);
                  startRetry();
                }}
                className="px-8 py-3 text-white rounded-lg font-semibold transition-colors shadow-lg"
                style={{ backgroundColor: '#ED1C24', boxShadow: '0 10px 15px -3px rgba(237, 28, 36, 0.2)' }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#D11820'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ED1C24'}
              >
                Review Incorrect Questions
              </button>
              <button
                onClick={() => {
                  setShowResultScreen(false);
                  startOver();
                }}
                className="block mx-auto mt-4 text-gray-500 hover:text-gray-700 underline"
              >
                Restart Entire Quiz
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 pt-4 px-4 sm:px-8">
        {/* Quiz Title with Hamburger Menu */}
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={onToggleSidebar}
            className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
            aria-label="Toggle menu"
          >
            <IonIcon icon={hamIcon} size="small" color="medium" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold">{course.quizTitle}</h1>
        </div>

      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-8 pb-8">
        <div id="lesson-progress">
          {/* Lesson Progress Indicator */}
          <div className="mb-3 flex items-center justify-between">
            <div>
              <span className="text-sm font-normal text-gray-600">STEP {stepNumber} OF {totalSteps}</span>
            </div>
            <div className="flex items-center">
              <div className={`mx-2 px-2 py-1.5 mt-1 text-xs font-medium rounded ${getStatusStyle()}`}>
                {lessonStatus}
              </div>
              <div className="flex gap-0.5">
                <IonButton
                  className="toolbar-button-icon-small"
                  fill="solid"
                  size="small"
                  color="medium"
                  onClick={(e) => handleNavigationClick('previous', e)}
                >
                  <IonIcon icon={chevronBackOutline} className="fs-16" color="secondary" />
                </IonButton>
                <IonButton
                  className="toolbar-button-icon-small"
                  fill="solid"
                  size="small"
                  color="medium"
                  disabled={!hasNextLesson}
                  onClick={(e) => handleNavigationClick('next', e)}
                >
                  <IonIcon icon={chevronForwardOutline} className="fs-16" color="secondary" />
                </IonButton>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          {currentProgress && currentProgress.percentComplete < 100 && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-gray-700">
                  <span className="hidden sm:inline">Progress: </span>{Math.round(currentProgress.percentComplete)}%
                </div>
                <div className="grow mx-4 h-2 bg-medium rounded-full overflow-hidden">
                  <div
                    className="w-full h-full bg-success rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${Math.min(Math.max(Math.round(currentProgress.percentComplete), 0), 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {currentQuestion && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300">

          <div className="p-6 md:p-6">
            {/* Question Header */}
            <div className="flex justify-between items-center mb-4">
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase">
                Question {currentQuestion.id}
              </span>
              {isCurrentAnswered && (
                <span className={`text-xl ${isCurrentCorrect ? 'text-green-500' : 'text-red-500'}`}>
                  {isCurrentCorrect ? '✅ Correct' : '❌ Incorrect'}
                </span>
              )}
            </div>

            {/* Question Text */}
            <h3 className="text-base md:text-xl font-medium mb-4 md:mb-3 leading-relaxed">
              {currentQuestion.text}
            </h3>

            {/* Options */}
            <div className="space-y-1 md:space-y-2">
              {currentQuestion.options.map((option: NormalizedQuizOption) => {
                const isSelected = userAnswers[currentQuestion.id] === option.id;
                const showResult = isCurrentAnswered;

                let optionClass = "w-full text-left px-3 py-2 md:p-3 rounded-lg border-2 transition-all duration-200 flex items-center group ";

                if (showResult) {
                  optionClass += "cursor-default ";
                  if (option.isCorrect) {
                    optionClass += "border-green-500 bg-green-50 ";
                  } else if (isSelected) {
                    optionClass += "border-red-500 bg-red-50 ";
                  } else {
                    optionClass += "border-gray-100 opacity-50 ";
                  }
                } else {
                  optionClass += "cursor-pointer hover:border-red-400 hover:bg-red-50 border-gray-200 ";
                  if (isSelected) optionClass += "border-red-500 bg-red-50 ring-2 ring-red-200 ring-offset-1 ";
                }

                return (
                  <button
                    key={option.id}
                    onClick={() => handleSelectAnswer(option.id)}
                    disabled={isCurrentAnswered}
                    className={optionClass}
                  >
                    {/* Radio Circle */}
                    <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center flex-shrink-0 transition-colors ${showResult
                        ? (option.isCorrect ? 'border-green-500 bg-green-500' : (isSelected ? 'border-red-500 bg-red-500' : 'border-gray-300'))
                        : (isSelected ? 'border-red-500 bg-red-500' : 'border-gray-300 group-hover:border-red-400')
                      }`}>
                      {/* Dot */}
                      <div className={`w-2 h-2 bg-white rounded-full ${isSelected || (showResult && option.isCorrect) ? 'opacity-100' : 'opacity-0'}`} />
                    </div>

                    <span className={`flex-1 min-w-0 text-left text-md md:text-lg pr-2 ${showResult && option.isCorrect ? 'font-semibold text-green-900' : 'text-gray-800'}`}>
                      {option.text}
                    </span>

                    {/* Result Icon - flex item so it stays separate from text */}
                    {showResult && (
                      <div className="flex-shrink-0 w-8 text-xl text-center" aria-hidden>
                        {option.isCorrect && '✓'}
                        {isSelected && !option.isCorrect && '✗'}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Explanation Section (Shows immediately after answering) */}
            {isCurrentAnswered && (
              <div className={`mt-6 p-4 rounded-lg border-l-4 animate-in fade-in slide-in-from-top-2 duration-300 ${isCurrentCorrect ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
                }`}>
                <div className="flex items-start">
                  <span className="text-2xl mr-3">{isCurrentCorrect ? '💡' : '⚠️'}</span>
                  <div>
                    <h4 className={`font-bold mb-1 ${isCurrentCorrect ? 'text-green-800' : 'text-red-800'}`}>
                      {isCurrentCorrect ? 'Great job!' : 'Not quite right'}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {/* Show the specific explanation for the selected option */}
                      {currentQuestion.options.find((o: NormalizedQuizOption) => o.id === userAnswers[currentQuestion.id])?.explanation}
                    </p>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Footer Navigation */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
              <div className="flex justify-between sm:contents">
                <button
                  onClick={handlePrev}
                  disabled={currentQuestionIndex === 0}
                  className="text-gray-500 font-medium px-4 py-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={isLastQuestion ? onFinishRound : handleNext}
                  disabled={!isCurrentAnswered}
                  className="bg-red-500 text-white px-8 py-2.5 rounded-lg font-semibold shadow-md shadow-red-200 hover:bg-red-600 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-red-500 transition-all"
                >
                  {isLastQuestion ? 'Finish Quiz' : 'Next Question'}
                </button>
              </div>
              <span className="text-sm font-semibold text-gray-400 uppercase tracking-wider text-center sm:text-left">
                Question {currentQuestionIndex + 1} of {activeQuestions.length}
              </span>
            </div>
          </div>

        </div>
        )}
      </div>

      {/* Navigation Confirmation Popover IF Course in Progress */}
      <PopoverMenuComponent
        isOpen={showNavigationPopover}
        event={navigationPopoverEvent}
        onDismiss={handlePopoverDismiss}
        menuItems={[
          {
            key: 'confirm',
            value: `Click to confirm you want to leave and go to the ${navigationDirection === 'next' ? 'next' : 'previous'} step.`
          }
        ]}
      />
    </div>
  );
};
